import React, { useState, useEffect, useCallback } from 'react';
import { displayStatusKey } from '../utils/claimStatus';
import './PaymentsPage.css';
import { formatPatientName } from '../utils/formatName';
import { DATE_INPUT_MIN, DATE_INPUT_MAX } from '../utils/dateInputBounds';
import HoldReasonsBadge from '../components/HoldReasonsBadge';
import PostHoldConfirm from '../components/PostHoldConfirm';

// The PM pulls (atlas-billing-ops) file 835s here as detail; the row says which PM.
const PM_SOURCE_LABELS = {
  practicefusion: 'Practice Fusion 835',
  practicesuite: 'PracticeSuite 835',
  advancedmd: 'AdvancedMD Waystar EOB',
};
// Detail-only sources a person may not post either: the PM already posted the cash.
const MANUAL_POST_REFUSED_SOURCES = ['practicesuite', 'advancedmd'];

const API_URL = process.env.REACT_APP_API_URL || '';

const claimStatusColors = {
  paid: '#2E9B6B',
  partial: '#D4AD5C',
  denied: '#C53030',
  accepted: '#2D4470',
  submitted: '#1B2A4A',
  rejected: '#C53030',
  draft: '#718096',
};

const uploadIcon = (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const pencilIcon = (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

function PaymentsPage({ selectedPractice, readOnly, isAdmin }) {
  const isClient = readOnly;
  // Top-level tabs: 'remittances' (insurance EOBs) | 'patient-payments' (cash/card/check)
  const [topTab, setTopTab] = useState('remittances');
  const [activeTab, setActiveTab] = useState(isClient ? 'posted' : 'unposted');
  const [remittances, setRemittances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedData, setExpandedData] = useState(null);
  const [message, setMessage] = useState(null);
  const [posting, setPosting] = useState(null); // remittance id being posted
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Counts for tab badges
  const [unpostedCount, setUnpostedCount] = useState(0);
  const [partialCount, setPartialCount] = useState(0);
  const [postedCount, setPostedCount] = useState(0);

  // Manual match modal state
  const [matchModal, setMatchModal] = useState(null); // { remittanceId, claimIndex, row }
  // "Post anyway?" — set when /post answered 409 with the hold reasons that
  // need a person's confirmation (duplicate ERA, claim already paid).
  const [postConfirm, setPostConfirm] = useState(null); // { remittanceId, reasons }
  const [matchSearch, setMatchSearch] = useState('');
  const [matchResults, setMatchResults] = useState([]);
  const [matchLoading, setMatchLoading] = useState(false);

  // Payment entry flow: 'closed' | 'choose' | 'upload' | 'manual'
  const [entryStep, setEntryStep] = useState('closed');

  // X12 upload modal state — supports multi-file
  // uploadItems: [{ file, preview, error, status: 'parsing'|'ready'|'error'|'saving'|'posting'|'posted'|'partial'|'unposted'|'failed', expanded, postOutcome }]
  const [uploadItems, setUploadItems] = useState([]);
  const [uploadCommitting, setUploadCommitting] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null); // { current, total, phase }

  // Manual EOB modal state — payment header. payer_db_id is the FK into
  // selectedPractice.payers; payer_name + payer_id_str are denormalized so we
  // can submit them and pass payer_id_str to the patient/line lookup endpoints.
  const [manualEob, setManualEob] = useState({
    payer_db_id: '',
    payer_name: '',
    payer_id_str: '',
    check_number: '',
    payment_method: 'CHK',
    payment_date: new Date().toISOString().slice(0, 10),
    total_amount: '',
  });
  // Manual EOB patient lookup + line entry
  const [eobPatientSearch, setEobPatientSearch] = useState('');
  const [eobPatientSuggestions, setEobPatientSuggestions] = useState([]);
  const [eobActivePatient, setEobActivePatient] = useState(null);
  const [eobActiveLines, setEobActiveLines] = useState([]);
  const [eobLinesLoading, setEobLinesLoading] = useState(false);
  const [eobPostingLog, setEobPostingLog] = useState([]);
  const [manualEobSubmitting, setManualEobSubmitting] = useState(false);
  const [manualEobError, setManualEobError] = useState(null);

  // Remittance edit modal — header-only edits (date, check, method, payer)
  const [editRem, setEditRem] = useState(null); // null | { id, payment_date, check_number, payment_method, payer_name }
  const [editRemSaving, setEditRemSaving] = useState(false);
  const [editRemError, setEditRemError] = useState(null);

  // Record Patient Payment modal state
  const [showPatientPmt, setShowPatientPmt] = useState(false);
  const [pmtStep, setPmtStep] = useState(1); // 1=details, 2=allocation
  const [pmtForm, setPmtForm] = useState({
    practice_patient_id: null,
    patient_label: '',
    amount: '',
    method: 'card',
    payment_date: new Date().toISOString().slice(0, 10),
    reference_number: '',
    notes: '',
  });
  const [pmtPatientSearch, setPmtPatientSearch] = useState('');
  const [pmtPatientResults, setPmtPatientResults] = useState([]);
  const [pmtPatientSearching, setPmtPatientSearching] = useState(false);
  const [pmtAllocations, setPmtAllocations] = useState([]); // [{claim_id, date_of_service, billed_amount, balance_before, applied}]
  const [pmtPreviewLoading, setPmtPreviewLoading] = useState(false);
  const [pmtSubmitting, setPmtSubmitting] = useState(false);
  const [pmtError, setPmtError] = useState(null);

  // Patient payments list state
  const [patientPayments, setPatientPayments] = useState([]);
  const [patientPaymentsLoading, setPatientPaymentsLoading] = useState(false);
  const [pmtExpandedId, setPmtExpandedId] = useState(null);
  const [pmtExpandedAllocations, setPmtExpandedAllocations] = useState(null);

  // Collections invoice modal state (admin-only)
  const _firstOfMonth = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
  };
  const _todayISO = () => new Date().toISOString().slice(0, 10);
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    start_date: _firstOfMonth(),
    end_date: _todayISO(),
    rate_pct: '',
  });
  const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);

  const practiceId = selectedPractice?.id;

  const loadCounts = useCallback(async () => {
    if (!practiceId) return;
    try {
      const fetches = [
        fetch(`${API_URL}/api/remittances?practice_id=${practiceId}&status=posted&per_page=1`, { credentials: 'include' }),
      ];
      if (!isClient) {
        fetches.unshift(
          fetch(`${API_URL}/api/remittances?practice_id=${practiceId}&status=unposted&per_page=1`, { credentials: 'include' }),
          fetch(`${API_URL}/api/remittances?practice_id=${practiceId}&status=partial&per_page=1`, { credentials: 'include' })
        );
      }
      const results = await Promise.all(fetches);
      if (isClient) {
        if (results[0].ok) { const d = await results[0].json(); setPostedCount(d.total || 0); }
      } else {
        if (results[0].ok) { const d = await results[0].json(); setUnpostedCount(d.total || 0); }
        if (results[1].ok) { const d = await results[1].json(); setPartialCount(d.total || 0); }
        if (results[2].ok) { const d = await results[2].json(); setPostedCount(d.total || 0); }
      }
    } catch (err) { /* ignore */ }
  }, [practiceId, isClient]);

  const loadRemittances = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 50, status: activeTab });
      if (practiceId) params.set('practice_id', practiceId);

      const response = await fetch(`${API_URL}/api/remittances?${params}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setRemittances(data.remittances || []);
        setTotalPages(data.pages || 1);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to load remittances:', err);
    } finally {
      setLoading(false);
    }
  }, [practiceId, page, activeTab]);

  useEffect(() => {
    loadRemittances();
    loadCounts();
  }, [loadRemittances, loadCounts]);

  // Reset page and expanded row when switching tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setExpandedId(null);
    setExpandedData(null);
  };

  const handleCheckNew = async () => {
    setChecking(true);
    setMessage(null);
    try {
      const response = await fetch(`${API_URL}/api/remittances/check-new`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ practice_id: selectedPractice.id }),
      });
      const data = await response.json();
      if (!response.ok) {
        setMessage({ type: 'error', text: data.error || 'Could not check for new ERAs.' });
        return;
      }
      const created = data.eras_created || 0;
      // Stedi reports duplicates as eras_skipped_duplicate; Claim.MD as eras_skipped.
      const dups = data.eras_skipped_duplicate || data.eras_skipped || 0;
      const posted = data.claims_posted || 0;
      const errs = data.errors || [];
      if (created > 0) {
        const notes = [];
        if (posted > 0) notes.push(`${posted} claim${posted === 1 ? '' : 's'} auto-posted`);
        if (dups > 0) notes.push(`${dups} duplicate${dups === 1 ? '' : 's'} skipped`);
        const suffix = notes.length ? ` (${notes.join(', ')})` : '';
        setMessage({
          type: 'success',
          text: `${created} new ERA${created === 1 ? '' : 's'} loaded${suffix}.`,
        });
        loadRemittances();
        loadCounts();
      } else if (errs.length > 0) {
        setMessage({
          type: 'error',
          text: `Encountered errors: ${errs.join('; ')}`,
        });
      } else if (dups > 0) {
        setMessage({
          type: 'success',
          text: `No new ERAs — ${dups} already on file.`,
        });
      } else {
        setMessage({
          type: 'success',
          text: 'No new ERAs found.',
        });
      }
    } catch (err) {
      setMessage({ type: 'error', text: `Error: ${err.message}` });
    } finally {
      setChecking(false);
    }
  };

  const handleInvoiceSubmit = async () => {
    setInvoiceError(null);
    if (!practiceId) {
      setInvoiceError('Select a practice first.');
      return;
    }
    if (!invoiceForm.start_date || !invoiceForm.end_date) {
      setInvoiceError('Both start and end dates are required.');
      return;
    }
    if (invoiceForm.end_date < invoiceForm.start_date) {
      setInvoiceError('End date must be on or after start date.');
      return;
    }
    const rateNum = parseFloat(invoiceForm.rate_pct);
    if (Number.isNaN(rateNum) || rateNum < 0 || rateNum > 100) {
      setInvoiceError('Rate must be a number between 0 and 100.');
      return;
    }

    setInvoiceSubmitting(true);
    try {
      const resp = await fetch(`${API_URL}/api/payments/invoice`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          practice_id: practiceId,
          start_date: invoiceForm.start_date,
          end_date: invoiceForm.end_date,
          rate_pct: rateNum,
        }),
      });
      if (!resp.ok) {
        let msg = `HTTP ${resp.status}`;
        try { const j = await resp.json(); if (j.error) msg = j.error; } catch (_) { /* not JSON */ }
        throw new Error(msg);
      }
      const cd = resp.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const filename = match ? decodeURIComponent(match[1]) : 'atlasinvoice.pdf';

      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setShowInvoice(false);
    } catch (err) {
      setInvoiceError(err.message);
    } finally {
      setInvoiceSubmitting(false);
    }
  };

  const handleRowClick = async (remittance) => {
    if (expandedId === remittance.id) {
      setExpandedId(null);
      setExpandedData(null);
      return;
    }

    setExpandedId(remittance.id);
    setExpandedData(null);
    try {
      const response = await fetch(`${API_URL}/api/remittances/${remittance.id}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setExpandedData(data);
      }
    } catch (err) {
      setExpandedData(null);
    }
  };

  const handlePost = async (remittanceId, { confirm = false } = {}) => {
    setPosting(remittanceId);
    setMessage(null);
    try {
      const response = await fetch(`${API_URL}/api/remittances/${remittanceId}/post`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm }),
      });
      const data = await response.json();
      if (response.status === 409 && data.requires_confirmation) {
        // Held as a duplicate or as paying an already-paid claim: ask first.
        setPostConfirm({ remittanceId, reasons: data.hold_reasons || [] });
        return;
      }
      setPostConfirm(null);
      if (data.success) {
        const unmatched = data.unmatched || [];
        if (unmatched.length > 0) {
          setMessage({
            type: 'success',
            text: `Posted ${data.claims_updated} claim(s). ${unmatched.length} unmatched — review in Partial tab.`,
          });
        } else {
          setMessage({
            type: 'success',
            text: `Posted! ${data.claims_updated} claim(s) updated in the ledger.`,
          });
        }
        setExpandedId(null);
        setExpandedData(null);
        loadRemittances();
        loadCounts();
      } else {
        setMessage({
          type: 'error',
          text: `Post failed: ${(data.errors || []).join('; ')}`,
        });
      }
    } catch (err) {
      setMessage({ type: 'error', text: `Error: ${err.message}` });
    } finally {
      setPosting(null);
    }
  };

  // The duplicate badge links to the other copy: open it where it lives
  // (Posted, Partial or Unposted) and expand it. Best effort — if it is not on
  // the first page of that tab the tab still switches.
  const jumpToRemittance = async (remittanceId) => {
    try {
      const response = await fetch(`${API_URL}/api/remittances/${remittanceId}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        setMessage({ type: 'error', text: `Could not open remittance #${remittanceId}.` });
        return;
      }
      const data = await response.json();
      if (['unposted', 'partial', 'posted'].includes(data.status)) {
        setActiveTab(data.status);
        setPage(1);
      }
      setExpandedId(remittanceId);
      setExpandedData(data);
    } catch (err) {
      setMessage({ type: 'error', text: `Error: ${err.message}` });
    }
  };

  const handleReverseRemittance = async (rem) => {
    const label = `${rem.payer_name || 'remittance'} (check ${rem.check_number || rem.id})`;
    const confirmed = window.confirm(
      `Reverse posting on ${label}?\n\n` +
      `This UNDOES all financial impact: it deletes adjudications, resets ` +
      `claim financials and statuses to 'submitted', and returns this row ` +
      `to the Unposted tab. The parsed claim list is preserved so you can ` +
      `re-post (or delete) afterwards.\n\n` +
      `Use this for batches that were Save & Posted by mistake or need ` +
      `re-matching.`
    );
    if (!confirmed) return;

    setMessage(null);
    try {
      const response = await fetch(`${API_URL}/api/remittances/${rem.id}/reverse`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMessage({
          type: 'success',
          text: `Reversed ${label}: ${data.claims_reset} claim(s) reset. Now in Unposted.`,
        });
        setExpandedId(null);
        setExpandedData(null);
        setActiveTab('unposted');
        loadRemittances();
        loadCounts();
      } else {
        setMessage({
          type: 'error',
          text: `Reverse failed: ${(data.errors || [data.error || 'unknown']).join('; ')}`,
        });
      }
    } catch (err) {
      setMessage({ type: 'error', text: `Error: ${err.message}` });
    }
  };

  // Remittance edit (header-only)
  const openEditRemittance = (rem) => {
    setEditRem({
      id: rem.id,
      payment_date: rem.payment_date || '',
      check_number: rem.check_number || '',
      payment_method: rem.payment_method || 'CHK',
      payer_name: rem.payer_name || '',
    });
    setEditRemError(null);
  };

  const updateEditRemField = (field, value) => {
    setEditRem((prev) => ({ ...prev, [field]: value }));
  };

  const submitEditRemittance = async () => {
    if (!editRem) return;
    setEditRemSaving(true);
    setEditRemError(null);
    try {
      const body = {
        payment_date: editRem.payment_date || null,
        check_number: editRem.check_number,
        payment_method: editRem.payment_method,
        payer_name: editRem.payer_name,
      };
      const response = await fetch(`${API_URL}/api/remittances/${editRem.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        setEditRemError(data.error || 'Edit failed');
        return;
      }
      setEditRem(null);
      setMessage({ type: 'success', text: 'Remittance updated.' });
      loadRemittances();
    } catch (err) {
      setEditRemError('Network error — please try again');
    } finally {
      setEditRemSaving(false);
    }
  };

  const handleDeleteRemittance = async (rem) => {
    const label = `${rem.payer_name || 'remittance'} (check ${rem.check_number || rem.id})`;
    const confirmed = window.confirm(
      `Delete ${label}?\n\nThis removes the remittance entirely. ` +
      `Only available for Unposted remittances; nothing has been posted to the ledger yet.`
    );
    if (!confirmed) return;

    setMessage(null);
    try {
      const response = await fetch(`${API_URL}/api/remittances/${rem.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMessage({ type: 'success', text: `Deleted ${label}.` });
        setExpandedId(null);
        setExpandedData(null);
        loadRemittances();
        loadCounts();
      } else {
        setMessage({ type: 'error', text: data.error || 'Delete failed' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: `Error: ${err.message}` });
    }
  };

  const handleManualMatch = async (remittanceId, claimIndex, claimId) => {
    setMessage(null);
    try {
      const response = await fetch(`${API_URL}/api/remittances/${remittanceId}/match-claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ parsed_claim_index: claimIndex, claim_id: claimId }),
      });
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Claim matched successfully.' });
        setMatchModal(null);
        handleRowClick({ id: remittanceId }); // refresh expanded data
        loadRemittances();
        loadCounts();
      } else {
        setMessage({ type: 'error', text: data.error || 'Match failed' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: `Error: ${err.message}` });
    }
  };

  const handleDismiss = async (remittanceId, claimIndex) => {
    setMessage(null);
    try {
      const response = await fetch(`${API_URL}/api/remittances/${remittanceId}/dismiss-unmatched`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ parsed_claim_index: claimIndex }),
      });
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Claim dismissed.' });
        handleRowClick({ id: remittanceId }); // refresh expanded data
        loadRemittances();
        loadCounts();
      } else {
        setMessage({ type: 'error', text: data.error || 'Dismiss failed' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: `Error: ${err.message}` });
    }
  };

  const searchClaimsForMatch = async (query) => {
    setMatchSearch(query);
    if (query.length < 2) { setMatchResults([]); return; }
    setMatchLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/remittances/search-claims?practice_id=${practiceId}&q=${encodeURIComponent(query)}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setMatchResults(data.claims || []);
      }
    } catch (err) { /* ignore */ }
    finally { setMatchLoading(false); }
  };

  // Manual EOB modal helpers
  const resetManualEob = () => {
    setManualEob({
      payer_db_id: '',
      payer_name: '',
      payer_id_str: '',
      check_number: '',
      payment_method: 'CHK',
      payment_date: new Date().toISOString().slice(0, 10),
      total_amount: '',
    });
    setEobPatientSearch('');
    setEobPatientSuggestions([]);
    setEobActivePatient(null);
    setEobActiveLines([]);
    setEobPostingLog([]);
    setManualEobError(null);
  };

  // Selecting a payer from the dropdown denormalizes name + payer_id onto
  // manualEob and clears any in-progress patient lookup so the next search
  // is scoped to the new payer.
  const selectEobPayer = (dbId) => {
    const payers = selectedPractice?.payers || [];
    const match = payers.find((p) => String(p.id) === String(dbId));
    setManualEob((prev) => ({
      ...prev,
      payer_db_id: dbId,
      payer_name: match ? match.payer_name : '',
      payer_id_str: match ? match.payer_id : '',
    }));
    setEobPatientSearch('');
    setEobPatientSuggestions([]);
    setEobActivePatient(null);
    setEobActiveLines([]);
  };

  const updateManualEobField = (field, value) => {
    setManualEob((prev) => ({ ...prev, [field]: value }));
  };

  // Patient autocomplete for manual EOB. Reuses /search-claims (tokenized,
  // payer-filtered); we de-dupe to one row per patient name.
  const searchEobPatients = async (query) => {
    setEobPatientSearch(query);
    if (!practiceId || query.trim().length < 2) {
      setEobPatientSuggestions([]);
      return;
    }
    try {
      const params = new URLSearchParams({
        practice_id: practiceId,
        q: query,
      });
      if (manualEob.payer_id_str) params.set('payer_id', manualEob.payer_id_str);
      const resp = await fetch(
        `${API_URL}/api/remittances/search-claims?${params}`,
        { credentials: 'include' }
      );
      if (!resp.ok) { setEobPatientSuggestions([]); return; }
      const data = await resp.json();
      const seen = new Set();
      const unique = [];
      for (const c of (data.claims || [])) {
        const name = (c.patient_name || '').trim();
        if (!name || seen.has(name.toLowerCase())) continue;
        seen.add(name.toLowerCase());
        unique.push(name);
        if (unique.length >= 8) break;
      }
      setEobPatientSuggestions(unique);
    } catch (err) {
      setEobPatientSuggestions([]);
    }
  };

  const loadEobLinesForPatient = async (patientName) => {
    if (!practiceId || !patientName) return;
    setEobActivePatient(patientName);
    setEobPatientSearch('');
    setEobPatientSuggestions([]);
    setEobLinesLoading(true);
    setEobActiveLines([]);
    try {
      const params = new URLSearchParams({
        practice_id: practiceId,
        patient_name: patientName,
      });
      if (manualEob.payer_id_str) params.set('payer_id', manualEob.payer_id_str);
      const resp = await fetch(
        `${API_URL}/api/remittances/manual/open-claim-lines?${params}`,
        { credentials: 'include' }
      );
      if (!resp.ok) {
        setEobActiveLines([]);
        return;
      }
      const data = await resp.json();
      const rows = (data.claim_lines || []).map((r) => ({
        ...r,
        _allowed: '',
        _paid: '',
        _pt_resp: '',
        _adj: '',
        _adj_manual: false,
      }));
      setEobActiveLines(rows);
      setEobPatientSearch(patientName);
    } catch (err) {
      setEobActiveLines([]);
    } finally {
      setEobLinesLoading(false);
    }
  };

  // Adj auto-computes from Charge - Allowed (or Charge - InsPaid - PtResp when
  // Allowed is blank). Typing into Adj sticks ("manual"); clearing it returns
  // to auto.
  const updateEobActiveLine = (index, field, value) => {
    setEobActiveLines((prev) => {
      const next = [...prev];
      const line = { ...next[index], [field]: value };
      if (field === '_adj') {
        line._adj_manual = value !== '';
      } else if (!line._adj_manual) {
        const charge = parseFloat(line.charge) || 0;
        const hasAllowed = line._allowed !== '' && line._allowed != null;
        const hasPaid = line._paid !== '' && line._paid != null;
        const hasPtResp = line._pt_resp !== '' && line._pt_resp != null;
        if (hasAllowed) {
          const allowed = parseFloat(line._allowed) || 0;
          const computed = Math.max(0, Math.round((charge - allowed) * 100) / 100);
          line._adj = computed.toFixed(2);
        } else if (hasPaid || hasPtResp) {
          const paid = parseFloat(line._paid) || 0;
          const pt = parseFloat(line._pt_resp) || 0;
          const computed = Math.max(0, Math.round((charge - paid - pt) * 100) / 100);
          line._adj = computed.toFixed(2);
        } else {
          line._adj = '';
        }
      }
      next[index] = line;
      return next;
    });
  };

  // Push the patient's filled-in lines into the Posting Log. Display row is
  // one per claim/DOS (billers think DOS-level), but each row carries its
  // per-CPT breakdown (`lines`) so the submit can attribute Adjudication rows
  // to specific ClaimLines on the backend.
  const postCurrentPatient = () => {
    if (!eobActivePatient) return;
    const groups = new Map();
    for (const line of eobActiveLines) {
      const paid = parseFloat(line._paid) || 0;
      const ptResp = parseFloat(line._pt_resp) || 0;
      const adj = parseFloat(line._adj) || 0;
      const allowed = parseFloat(line._allowed) || 0;
      if (!line._paid && !line._pt_resp && !line._adj && !line._allowed) continue;
      const key = line.claim_id;
      const existing = groups.get(key) || {
        patient_name: eobActivePatient,
        claim_id: line.claim_id,
        claim_control_number: line.claim_control_number,
        date_of_service: line.date_of_service,
        charge: 0,
        allowed: 0,
        paid: 0,
        pt_resp: 0,
        adj: 0,
        lines: [],
      };
      existing.charge += parseFloat(line.charge) || 0;
      existing.allowed += allowed;
      existing.paid += paid;
      existing.pt_resp += ptResp;
      existing.adj += adj;
      existing.lines.push({
        cpt_code: line.cpt_code,
        modifiers: line.modifiers || [],
        units: line.units || 1,
        charge: parseFloat(line.charge) || 0,
        allowed,
        paid,
        pt_resp: ptResp,
        adj,
      });
      groups.set(key, existing);
    }
    const entries = Array.from(groups.values()).map((g) => ({
      ...g,
      charge: Math.round(g.charge * 100) / 100,
      allowed: Math.round(g.allowed * 100) / 100,
      paid: Math.round(g.paid * 100) / 100,
      pt_resp: Math.round(g.pt_resp * 100) / 100,
      adj: Math.round(g.adj * 100) / 100,
    }));
    if (entries.length === 0) {
      setManualEobError('Enter at least one amount before posting this patient.');
      return;
    }
    setManualEobError(null);
    setEobPostingLog((prev) => [...prev, ...entries]);
    setEobActivePatient(null);
    setEobActiveLines([]);
    setEobPatientSearch('');
  };

  const removePostingLogEntry = (index) => {
    setEobPostingLog((prev) => prev.filter((_, i) => i !== index));
  };

  // Patient payment helpers
  const resetPatientPmt = () => {
    setPmtForm({
      practice_patient_id: null,
      patient_label: '',
      amount: '',
      method: 'card',
      payment_date: new Date().toISOString().slice(0, 10),
      reference_number: '',
      notes: '',
    });
    setPmtPatientSearch('');
    setPmtPatientResults([]);
    setPmtAllocations([]);
    setPmtError(null);
    setPmtStep(1);
  };

  const searchPatients = async (query) => {
    setPmtPatientSearch(query);
    if (!practiceId || query.length < 2) { setPmtPatientResults([]); return; }
    setPmtPatientSearching(true);
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/patients?search=${encodeURIComponent(query)}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        // Endpoint returns {patients: [...]} or array — handle both
        const list = Array.isArray(data) ? data : (data.patients || []);
        setPmtPatientResults(list.slice(0, 10));
      }
    } catch (err) { /* ignore */ }
    finally { setPmtPatientSearching(false); }
  };

  const selectPatient = (patient) => {
    const label = (patient.subscriber_first_name || patient.patient_first_name || '') + ' ' + (patient.subscriber_last_name || patient.patient_last_name || '');
    setPmtForm((prev) => ({
      ...prev,
      practice_patient_id: patient.id,
      patient_label: label.trim(),
    }));
    setPmtPatientSearch('');
    setPmtPatientResults([]);
  };

  const goToAllocationStep = async () => {
    setPmtError(null);
    if (!pmtForm.practice_patient_id) { setPmtError('Pick a patient'); return; }
    const amt = parseFloat(pmtForm.amount);
    if (!amt || amt <= 0) { setPmtError('Amount must be greater than zero'); return; }
    if (!pmtForm.payment_date) { setPmtError('Payment date is required'); return; }

    setPmtPreviewLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/payments/preview-allocation`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          practice_id: practiceId,
          practice_patient_id: pmtForm.practice_patient_id,
          amount: amt,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setPmtError(data.error || 'Failed to compute allocation');
        return;
      }
      setPmtAllocations(data.allocations || []);
      setPmtStep(2);
    } catch (err) {
      setPmtError('Network error — please try again');
    } finally {
      setPmtPreviewLoading(false);
    }
  };

  const updateAllocationAmount = (idx, value) => {
    setPmtAllocations((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], applied: value };
      return next;
    });
  };

  const allocationSum = pmtAllocations.reduce((s, a) => s + (parseFloat(a.applied) || 0), 0);
  const pmtAmount = parseFloat(pmtForm.amount) || 0;

  const handlePatientPmtSubmit = async () => {
    setPmtError(null);
    const allocations = pmtAllocations
      .filter((a) => parseFloat(a.applied) > 0)
      .map((a) => ({ claim_id: a.claim_id, amount: parseFloat(a.applied) }));
    if (allocationSum > pmtAmount + 0.005) {
      setPmtError(`Allocation total ($${allocationSum.toFixed(2)}) exceeds payment amount ($${pmtAmount.toFixed(2)})`);
      return;
    }

    setPmtSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/payments/patient`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          practice_id: practiceId,
          practice_patient_id: pmtForm.practice_patient_id,
          amount: pmtAmount,
          method: pmtForm.method,
          payment_date: pmtForm.payment_date,
          reference_number: pmtForm.reference_number || null,
          notes: pmtForm.notes || null,
          allocations,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setPmtError(data.error || 'Failed to record payment');
        return;
      }

      setShowPatientPmt(false);
      resetPatientPmt();
      setMessage({ type: 'success', text: `Payment recorded for ${pmtForm.patient_label} ($${pmtAmount.toFixed(2)}).` });
      loadPatientPayments();
    } catch (err) {
      setPmtError('Network error — please try again');
    } finally {
      setPmtSubmitting(false);
    }
  };

  const loadPatientPayments = useCallback(async () => {
    if (!practiceId) return;
    setPatientPaymentsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/payments/patient?practice_id=${practiceId}&per_page=50`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setPatientPayments(data.patient_payments || []);
      }
    } catch (err) { /* ignore */ }
    finally { setPatientPaymentsLoading(false); }
  }, [practiceId]);

  // Load patient payments when switching to that tab
  useEffect(() => {
    if (topTab === 'patient-payments') {
      loadPatientPayments();
    }
  }, [topTab, loadPatientPayments]);

  const togglePatientPmtRow = async (paymentId) => {
    if (pmtExpandedId === paymentId) {
      setPmtExpandedId(null);
      setPmtExpandedAllocations(null);
      return;
    }
    setPmtExpandedId(paymentId);
    setPmtExpandedAllocations(null);
    try {
      const response = await fetch(
        `${API_URL}/api/payments/patient/${paymentId}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setPmtExpandedAllocations(data.allocations || []);
      }
    } catch (err) { /* ignore */ }
  };

  const handleVoidPatientPayment = async (p) => {
    const label = `${formatPatientName(p.patient_name)} ${formatCurrency(p.amount)} (${p.method})`;
    const confirmed = window.confirm(
      `Void payment for ${label}?\n\n` +
      `This reverses the allocations: each claim's patient_payment is ` +
      `decreased by what was applied, and any claim_status='paid' that now ` +
      `has a remaining balance flips back to 'patient'.\n\n` +
      `The payment row stays for the audit trail (status='voided').`
    );
    if (!confirmed) return;
    setMessage(null);
    try {
      const resp = await fetch(`${API_URL}/api/payments/patient/${p.id}/void`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        setMessage({
          type: 'success',
          text: `Voided ${label}: ${data.claims_reverted} claim(s) restored.`,
        });
        setPmtExpandedId(null);
        setPmtExpandedAllocations(null);
        loadPatientPayments();
      } else {
        setMessage({
          type: 'error',
          text: `Void failed: ${(data.errors || [data.error || 'unknown']).join('; ')}`,
        });
      }
    } catch (err) {
      setMessage({ type: 'error', text: `Error: ${err.message}` });
    }
  };

  const handleManualEobSubmit = async () => {
    if (!practiceId) { setManualEobError('No practice selected'); return; }
    if (!manualEob.payer_name) {
      setManualEobError('Select a payer.');
      return;
    }
    if (eobPostingLog.length === 0) {
      setManualEobError('Post at least one patient to the log before saving.');
      return;
    }
    setManualEobSubmitting(true);
    setManualEobError(null);
    try {
      // Flatten posting-log entries into one wire item per CPT line so the
      // backend can build Adjudication rows attributed to specific ClaimLines.
      const lineItems = [];
      for (const entry of eobPostingLog) {
        for (const ln of (entry.lines || [])) {
          lineItems.push({
            claim_id: entry.claim_id,
            patient_name: entry.patient_name,
            service_date: entry.date_of_service,
            claim_control_number: entry.claim_control_number || null,
            cpt_code: ln.cpt_code,
            modifiers: ln.modifiers || [],
            units: ln.units || 1,
            charge: Math.round((parseFloat(ln.charge) || 0) * 100) / 100,
            allowed: Math.round((parseFloat(ln.allowed) || 0) * 100) / 100,
            paid: Math.round((parseFloat(ln.paid) || 0) * 100) / 100,
            pt_resp: Math.round((parseFloat(ln.pt_resp) || 0) * 100) / 100,
            adj: Math.round((parseFloat(ln.adj) || 0) * 100) / 100,
          });
        }
      }

      const payload = {
        practice_id: practiceId,
        payer_name: manualEob.payer_name.trim(),
        check_number: manualEob.check_number.trim(),
        payment_method: manualEob.payment_method,
        payment_date: manualEob.payment_date,
        total_amount: parseFloat(manualEob.total_amount) || 0,
        line_items: lineItems,
      };

      const response = await fetch(`${API_URL}/api/remittances/manual`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        setManualEobError(data.error || 'Failed to create manual EOB');
        return;
      }

      setEntryStep('closed');
      resetManualEob();
      setMessage({ type: 'success', text: `Manual EOB created — review in Unposted (${lineItems.length} claim${lineItems.length === 1 ? '' : 's'}).` });
      setActiveTab('unposted');
      loadCounts();
      loadRemittances();
    } catch (err) {
      setManualEobError('Network error — please try again');
    } finally {
      setManualEobSubmitting(false);
    }
  };

  // X12 .dat upload handlers (supports multi-file)
  const _parseOneFile = async (file) => {
    const fd = new FormData();
    fd.append('practice_id', practiceId);
    fd.append('file', file);
    const response = await fetch(`${API_URL}/api/remittances/parse-x12`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    const data = await response.json();
    if (!response.ok) {
      const err = new Error(data.error || 'Failed to parse file');
      err.serverMessage = data.error;
      throw err;
    }
    return data;
  };

  const handleUploadFilesChange = async (files) => {
    if (!files || files.length === 0) return;
    if (!practiceId) { setUploadError('No practice selected'); return; }

    setUploadError(null);
    // Add new entries (parsing) to the list — don't replace existing ones, so
    // a user can drop more files after a first batch.
    const newItems = Array.from(files).map((f) => ({
      file: f,
      preview: null,
      error: null,
      status: 'parsing',
      expanded: false,
    }));
    setUploadItems((prev) => [...prev, ...newItems]);

    // Parse all in parallel
    await Promise.all(
      newItems.map(async (item) => {
        try {
          const preview = await _parseOneFile(item.file);
          setUploadItems((prev) =>
            prev.map((it) =>
              it.file === item.file
                ? { ...it, preview, status: 'ready' }
                : it
            )
          );
        } catch (err) {
          setUploadItems((prev) =>
            prev.map((it) =>
              it.file === item.file
                ? { ...it, error: err.serverMessage || 'Network error', status: 'error' }
                : it
            )
          );
        }
      })
    );
  };

  const removeUploadItem = (file) => {
    setUploadItems((prev) => prev.filter((it) => it.file !== file));
  };

  const toggleUploadItemExpanded = (file) => {
    setUploadItems((prev) =>
      prev.map((it) =>
        it.file === file ? { ...it, expanded: !it.expanded } : it
      )
    );
  };

  const handleUploadCommit = async () => {
    return _commitBatch({ andPost: false });
  };

  const handleUploadCommitAndPost = async () => {
    return _commitBatch({ andPost: true });
  };

  const _setItemStatus = (file, patch) => {
    setUploadItems((prev) =>
      prev.map((it) => (it.file === file ? { ...it, ...patch } : it))
    );
  };

  // Number of files processed concurrently. Backend per-claim idempotency
  // and natural-key dedup make concurrent posts safe; 4 is a reasonable
  // default that pipelines DB I/O without overwhelming the database's connection
  // pool. Bump if the network is the bottleneck.
  const _COMMIT_CONCURRENCY = 4;

  const _processOneFile = async (item, andPost) => {
    _setItemStatus(item.file, { status: 'saving' });
    try {
      const fd = new FormData();
      fd.append('practice_id', practiceId);
      fd.append('file', item.file);
      const response = await fetch(`${API_URL}/api/remittances/upload-x12`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await response.json();
      if (!response.ok) {
        const errMsg = data.error || `HTTP ${response.status}`;
        _setItemStatus(item.file, { status: 'failed', error: errMsg });
        return { outcome: 'failed', name: item.file.name, error: errMsg };
      }

      if (andPost && data.id) {
        _setItemStatus(item.file, { status: 'posting' });
        try {
          const postResp = await fetch(`${API_URL}/api/remittances/${data.id}/post`, {
            method: 'POST',
            credentials: 'include',
          });
          const postData = await postResp.json();
          if (postResp.ok && postData.success) {
            if ((postData.unmatched || []).length > 0) {
              _setItemStatus(item.file, { status: 'partial' });
              return { outcome: 'partial', claims: data.claims_count || 0 };
            }
            _setItemStatus(item.file, { status: 'posted' });
            return { outcome: 'posted', claims: data.claims_count || 0 };
          }
          _setItemStatus(item.file, { status: 'unposted' });
          return { outcome: 'skipped', claims: data.claims_count || 0 };
        } catch (err) {
          _setItemStatus(item.file, { status: 'unposted' });
          return { outcome: 'skipped', claims: data.claims_count || 0 };
        }
      }

      _setItemStatus(item.file, { status: 'unposted' });
      return { outcome: 'imported_only', claims: data.claims_count || 0 };
    } catch (err) {
      _setItemStatus(item.file, { status: 'failed', error: 'Network error' });
      return { outcome: 'failed', name: item.file.name, error: 'Network error' };
    }
  };

  const _commitBatch = async ({ andPost }) => {
    const ready = uploadItems.filter((it) => it.status === 'ready');
    if (ready.length === 0 || !practiceId) return;

    setUploadCommitting(true);
    setUploadError(null);
    setUploadProgress({ current: 0, total: ready.length, phase: andPost ? 'posting' : 'saving' });

    let imported = 0;
    let totalClaims = 0;
    let postedFully = 0;
    let postedPartial = 0;
    let postedSkipped = 0;
    let completed = 0;
    const failures = [];

    // Worker-pool style: process up to _COMMIT_CONCURRENCY files at a time.
    // Backend per-claim idempotency + natural-key dedup keep concurrent
    // operations safe. Each worker pulls the next index, processes it,
    // updates per-item status, and increments the global counter.
    let nextIndex = 0;
    const worker = async () => {
      while (true) {
        const idx = nextIndex++;
        if (idx >= ready.length) return;
        const r = await _processOneFile(ready[idx], andPost);
        completed += 1;
        setUploadProgress({
          current: completed,
          total: ready.length,
          phase: andPost ? 'posting' : 'saving',
        });
        switch (r.outcome) {
          case 'failed':
            failures.push({ name: r.name, error: r.error });
            break;
          case 'posted':
            imported += 1;
            totalClaims += r.claims;
            postedFully += 1;
            break;
          case 'partial':
            imported += 1;
            totalClaims += r.claims;
            postedPartial += 1;
            break;
          case 'skipped':
            imported += 1;
            totalClaims += r.claims;
            postedSkipped += 1;
            break;
          case 'imported_only':
            imported += 1;
            totalClaims += r.claims;
            break;
          default:
            break;
        }
      }
    };

    const workers = Array.from(
      { length: Math.min(_COMMIT_CONCURRENCY, ready.length) },
      () => worker()
    );
    await Promise.all(workers);

    setUploadCommitting(false);
    setUploadProgress(null);

    const failureBlurb = failures.length
      ? ` ${failures.length} failed: ${failures.map((f) => `${f.name} (${f.error})`).join('; ')}.`
      : '';

    if (imported === 0 && failures.length > 0) {
      setMessage({
        type: 'error',
        text: `All ${failures.length} file${failures.length === 1 ? '' : 's'} failed: ${failures.map((f) => `${f.name} (${f.error})`).join('; ')}`,
      });
    } else if (andPost) {
      const parts = [];
      if (postedFully) parts.push(`${postedFully} fully posted`);
      if (postedPartial) parts.push(`${postedPartial} partial (review unmatched)`);
      if (postedSkipped) parts.push(`${postedSkipped} unposted (no matches)`);
      setMessage({
        type: 'success',
        text: `Imported ${imported} ERA${imported === 1 ? '' : 's'} (${totalClaims} claims): ${parts.join(', ') || 'none posted'}.${failureBlurb}`,
      });
      if (postedPartial > 0) setActiveTab('partial');
      else if (postedFully > 0) setActiveTab('posted');
      else setActiveTab('unposted');
    } else {
      setMessage({
        type: 'success',
        text: `Imported ${imported} ERA${imported === 1 ? '' : 's'} — ${totalClaims} claim${totalClaims === 1 ? '' : 's'} ready to post in Unposted.${failureBlurb}`,
      });
      setActiveTab('unposted');
    }

    setEntryStep('closed');
    setUploadItems([]);
    loadCounts();
    loadRemittances();
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '—';
    return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    let s = String(dateStr);
    // 8-digit YYYYMMDD (some ERA service_date values come back this way) → YYYY-MM-DD
    if (/^\d{8}$/.test(s)) s = `${s.slice(0,4)}-${s.slice(4,6)}-${s.slice(6,8)}`;
    const d = new Date(s + (s.includes('T') ? '' : 'T00:00:00'));
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Render the expanded detail section for a remittance
  const renderDetail = (rem) => {
    if (!expandedData) {
      return (
        <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '13px' }}>
          Loading...
        </div>
      );
    }

    // Always show parsed 835 data — this is the authoritative ERA source
    const showMatchCol = rem.status === 'unposted' || rem.status === 'partial';
    const claimRows = expandedData.parsed_claims || [];

    if (claimRows.length === 0) {
      return (
        <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '13px' }}>
          Informational ERA — no claim payments (e.g. bonus, capitation, or administrative payment).
        </div>
      );
    }

    return (
      <>
        <table className="remittance-claims-table">
          <thead>
            <tr>
              {showMatchCol && <th>Status</th>}
              <th>Patient</th>
              <th>DOS</th>
              <th className="col-currency">Billed</th>
              <th className="col-currency">Adj</th>
              <th className="col-currency">Pt Resp</th>
              <th className="col-currency">Paid</th>
              <th>Codes</th>
              {(rem.status === 'partial' || rem.status === 'unposted') && !readOnly && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {claimRows.map((row, idx) => {
              const matched = !!row.matched_claim_id;
              const dismissed = !!row.dismissed;
              return (
                <tr key={idx} className={dismissed ? 'dismissed-row' : ''}>
                  {showMatchCol && (
                    <td>
                      {dismissed ? (
                        <span className="match-status dismissed">Dismissed</span>
                      ) : matched ? (
                        <span className="match-status matched">Matched</span>
                      ) : (
                        <span className="match-status unmatched">Unmatched</span>
                      )}
                    </td>
                  )}
                  <td>{row.patient_name ? formatPatientName(row.patient_name) : (row.patient_control_number || '—')}</td>
                  <td>{formatDate(row.service_date)}</td>
                  <td className="col-currency">{formatCurrency(row.total_charge_amount)}</td>
                  <td className="col-currency">{formatCurrency(row.co_adjustments)}</td>
                  <td className="col-currency">{formatCurrency(row.pr_adjustments)}</td>
                  <td className="col-currency">{formatCurrency(row.claim_payment_amount)}</td>
                  <td>
                    {(row.carc_codes || []).map((c, i) => (
                      <span key={i} className="code-chip carc">{c}</span>
                    ))}
                    {(row.rarc_codes || []).map((c, i) => (
                      <span key={i} className="code-chip rarc">{c}</span>
                    ))}
                    {!(row.carc_codes || []).length && !(row.rarc_codes || []).length && '—'}
                  </td>
                  {(rem.status === 'partial' || rem.status === 'unposted') && !readOnly && (
                    <td>
                      {!matched && !dismissed && (
                        <div className="unmatched-actions">
                          <button
                            className="btn btn-primary btn-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMatchModal({ remittanceId: rem.id, claimIndex: idx, row });
                              setMatchSearch('');
                              setMatchResults([]);
                            }}
                          >
                            Match
                          </button>
                          <button
                            className="btn btn-secondary btn-xs"
                            onClick={(e) => { e.stopPropagation(); handleDismiss(rem.id, idx); }}
                          >
                            Dismiss
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="remittance-detail-actions">
          <button
            className="btn btn-secondary btn-sm"
            onClick={async (e) => {
              e.stopPropagation();
              try {
                const resp = await fetch(`${API_URL}/api/remittances/${rem.id}/pdf`, { credentials: 'include' });
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const blob = await resp.blob();
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
              } catch (err) {
                setMessage({ type: 'error', text: `Failed to load PDF: ${err.message}` });
              }
            }}
          >
            View PDF
          </button>
          {!readOnly && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={(e) => { e.stopPropagation(); openEditRemittance(rem); }}
              title="Edit payment date, check #, method, or payer (header only — does not change line-item amounts)."
            >
              Edit
            </button>
          )}
          {rem.status === 'unposted' && !readOnly && (
            <button
              className="btn btn-secondary btn-sm btn-danger"
              onClick={(e) => { e.stopPropagation(); handleDeleteRemittance(rem); }}
            >
              Delete
            </button>
          )}
          {(rem.status === 'partial' || rem.status === 'posted') && !readOnly && (
            <button
              className="btn btn-secondary btn-sm btn-danger"
              onClick={(e) => { e.stopPropagation(); handleReverseRemittance(rem); }}
              title="Undo this remittance's posting: deletes adjudications, resets claim financials, returns the row to Unposted. Preserves the parsed claim list so you can re-post."
            >
              Reverse Posting
            </button>
          )}
          {(rem.status === 'unposted' || rem.status === 'partial') && !readOnly
            && !MANUAL_POST_REFUSED_SOURCES.includes(rem.source) && (
            <button
              className="btn-post"
              onClick={(e) => { e.stopPropagation(); handlePost(rem.id); }}
              disabled={posting === rem.id}
              title={
                rem.status === 'partial'
                  ? 'Post any newly-matched claims to the ledger. Already-posted claims are skipped (no duplicates).'
                  : 'Post all matched claims to the ledger.'
              }
            >
              {posting === rem.id
                ? 'Posting...'
                : rem.status === 'partial'
                  ? 'Post Newly Matched'
                  : 'Post to Ledger'}
            </button>
          )}
        </div>
      </>
    );
  };

  const renderTable = () => {
    if (loading) {
      return (
        <div className="remittances-loading">
          <div className="spinner"></div>
          <span>Loading remittances...</span>
        </div>
      );
    }

    if (remittances.length === 0) {
      return (
        <div className="remittances-empty">
          {activeTab === 'unposted' ? (
            <>
              <p>No unposted ERAs.</p>
              <p>Click "Check for new ERAs" to poll Stedi for 835 payment reports.</p>
            </>
          ) : activeTab === 'partial' ? (
            <p>No partially posted remittances.</p>
          ) : (
            <p>No posted remittances yet.</p>
          )}
        </div>
      );
    }

    return (
      <>
        <div className="remittances-table-wrapper">
          <table className="remittances-table">
            <thead>
              <tr>
                <th>Date Received</th>
                <th>Payment Date</th>
                <th>Payer</th>
                <th>Check/EFT #</th>
                <th style={{ textAlign: 'right' }}>Total Amount</th>
                <th style={{ textAlign: 'center' }}>Claims</th>
                {activeTab === 'unposted' && <th>Review</th>}
                {(activeTab === 'posted' || activeTab === 'partial') && <th>Posted</th>}
              </tr>
            </thead>
            <tbody>
              {remittances.map((rem) => (
                <React.Fragment key={rem.id}>
                  <tr
                    className={`remittance-row ${expandedId === rem.id ? 'expanded' : ''}`}
                    onClick={() => handleRowClick(rem)}
                  >
                    <td>{formatDate(rem.created_at)}</td>
                    <td>{formatDate(rem.payment_date)}</td>
                    <td>
                      {rem.payer_name || '—'}
                      {PM_SOURCE_LABELS[rem.source] && (
                        <div className="remittance-source" data-testid="remittance-source">
                          Source: {PM_SOURCE_LABELS[rem.source]}
                          {rem.pf_status ? ` · ${rem.pf_status}` : ''}
                        </div>
                      )}
                    </td>
                    <td className="col-mono">{rem.check_number || '—'}</td>
                    <td className="col-currency">{formatCurrency(rem.total_amount)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`match-ratio ${activeTab === 'posted' ? 'full' : activeTab === 'partial' ? 'partial' : 'none'}`}>
                        {rem.claims_count}
                      </span>
                    </td>
                    {activeTab === 'unposted' && (
                      <td className="col-review">
                        <HoldReasonsBadge reasons={rem.hold_reasons} onJump={jumpToRemittance} />
                      </td>
                    )}
                    {(activeTab === 'posted' || activeTab === 'partial') && (
                      <td>{formatDate(rem.posted_at)}</td>
                    )}
                  </tr>
                  {expandedId === rem.id && (
                    <tr className="remittance-detail-row">
                      <td colSpan={7}>
                        {renderDetail(rem)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="remittances-pagination">
            <button
              className="btn btn-secondary btn-sm"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              Prev
            </button>
            <span className="pagination-info">
              Page {page} of {totalPages}
            </span>
            <button
              className="btn btn-secondary btn-sm"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </>
    );
  };

  const hasIntegratedClearinghouse =
    selectedPractice && (selectedPractice.clearinghouse === 'stedi' || selectedPractice.clearinghouse === 'claimmd');

  return (
    <div className="remittances-page payments-page">
      <div className="remittances-header">
        <h1 className="page-title">Payments</h1>
        <div className="remittances-header-actions">
          {!readOnly && topTab === 'remittances' && hasIntegratedClearinghouse && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleCheckNew}
              disabled={checking}
            >
              {checking ? 'Checking...' : 'Check for new ERAs'}
            </button>
          )}
          {!readOnly && topTab === 'remittances' && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setEntryStep('choose')}
            >
              Enter Payment
            </button>
          )}
          {!readOnly && topTab === 'patient-payments' && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => { resetPatientPmt(); setShowPatientPmt(true); }}
            >
              Record Patient Payment
            </button>
          )}
          {isAdmin && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={async () => {
                setInvoiceError(null);
                setShowInvoice(true);
                if (practiceId) {
                  try {
                    const resp = await fetch(`${API_URL}/api/practices/${practiceId}`, { credentials: 'include' });
                    if (resp.ok) {
                      const data = await resp.json();
                      const pct = data.collections_fee_pct;
                      if (pct !== null && pct !== undefined) {
                        setInvoiceForm((f) => ({ ...f, rate_pct: String(pct) }));
                      }
                    }
                  } catch (_) { /* prefill is best-effort */ }
                }
              }}
            >
              Invoice
            </button>
          )}
        </div>
      </div>

      <div className="payments-top-tabs">
        <button
          className={`payments-top-tab ${topTab === 'remittances' ? 'active' : ''}`}
          onClick={() => setTopTab('remittances')}
        >
          Remittances
        </button>

        {topTab === 'remittances' && !isClient && (
          <>
            <button
              className={`payments-sub-tab ${activeTab === 'unposted' ? 'active' : ''}`}
              onClick={() => handleTabChange('unposted')}
            >
              Unposted <span className="tab-count">{unpostedCount}</span>
            </button>
            {partialCount > 0 && (
              <button
                className={`payments-sub-tab ${activeTab === 'partial' ? 'active' : ''}`}
                onClick={() => handleTabChange('partial')}
              >
                Partial <span className="tab-count partial-count">{partialCount}</span>
              </button>
            )}
            <button
              className={`payments-sub-tab ${activeTab === 'posted' ? 'active' : ''}`}
              onClick={() => handleTabChange('posted')}
            >
              Posted <span className="tab-count">{postedCount}</span>
            </button>
          </>
        )}

        <button
          className={`payments-top-tab ${topTab === 'patient-payments' ? 'active' : ''}`}
          onClick={() => setTopTab('patient-payments')}
        >
          Patient Payments
        </button>
      </div>

      {message && (
        <div className={`remittances-message ${message.type}`}>
          <span>{message.text}</span>
          <button className="close-btn" onClick={() => setMessage(null)}>&times;</button>
        </div>
      )}

      {topTab === 'remittances' && renderTable()}

      {topTab === 'patient-payments' && (
        <div className="patient-payments-section">

          {patientPaymentsLoading && (
            <div className="remittances-loading"><div className="spinner"></div><div>Loading…</div></div>
          )}

          {!patientPaymentsLoading && patientPayments.length === 0 && (
            <div className="remittances-empty">
              <p>No patient payments recorded yet.</p>
            </div>
          )}

          {!patientPaymentsLoading && patientPayments.length > 0 && (
            <div className="remittances-table-wrapper">
              <table className="remittances-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Patient</th>
                    <th>Method</th>
                    <th className="col-currency">Amount</th>
                    <th>Reference</th>
                    <th>Posted By</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {patientPayments.map((p) => {
                    const isVoided = p.status === 'voided';
                    return (
                    <React.Fragment key={p.id}>
                      <tr
                        className={`remittance-row ${pmtExpandedId === p.id ? 'expanded' : ''} ${isVoided ? 'dismissed-row' : ''}`}
                        onClick={() => togglePatientPmtRow(p.id)}
                      >
                        <td>{formatDate(p.payment_date)}</td>
                        <td>{formatPatientName(p.patient_name)}</td>
                        <td style={{ textTransform: 'capitalize' }}>{p.method}</td>
                        <td className="col-currency">{formatCurrency(p.amount)}</td>
                        <td className="col-mono">{p.reference_number || '—'}</td>
                        <td>{p.posted_by_name || '—'}</td>
                        <td>
                          {isVoided ? (
                            <span className="match-status dismissed">Voided</span>
                          ) : (
                            <span className="match-status matched">Posted</span>
                          )}
                        </td>
                      </tr>
                      {pmtExpandedId === p.id && (
                        <tr className="remittance-detail-row">
                          <td colSpan={7}>
                            {!pmtExpandedAllocations && <div>Loading allocation…</div>}
                            {pmtExpandedAllocations && pmtExpandedAllocations.length === 0 && (
                              <div>No allocations recorded.</div>
                            )}
                            {pmtExpandedAllocations && pmtExpandedAllocations.length > 0 && (
                              <table className="remittance-claims-table">
                                <thead>
                                  <tr>
                                    <th>Claim DOS</th>
                                    <th className="col-currency">Claim Billed</th>
                                    <th className="col-currency">Applied</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {pmtExpandedAllocations.map((a) => (
                                    <tr key={a.id}>
                                      <td>{formatDate(a.claim_date_of_service)}</td>
                                      <td className="col-currency">{formatCurrency(a.claim_billed_amount)}</td>
                                      <td className="col-currency">{formatCurrency(a.amount)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            {!isVoided && !readOnly && (
                              <div className="remittance-detail-actions">
                                <button
                                  className="btn btn-secondary btn-sm btn-danger"
                                  onClick={(e) => { e.stopPropagation(); handleVoidPatientPayment(p); }}
                                  title="Reverse this payment. Restores claim balances and flips claim_status from 'paid' back to 'patient' if needed."
                                >
                                  Void Payment
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {postConfirm && (
        <PostHoldConfirm
          reasons={postConfirm.reasons}
          busy={posting === postConfirm.remittanceId}
          onCancel={() => setPostConfirm(null)}
          onConfirm={() => handlePost(postConfirm.remittanceId, { confirm: true })}
        />
      )}

      {matchModal && (
        <div className="modal-overlay" onClick={() => setMatchModal(null)}>
          <div className="modal-content match-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Match Claim</h3>
              <button className="close-btn" onClick={() => setMatchModal(null)}>&times;</button>
            </div>
            <div className="match-modal-context">
              <p>
                <strong>{formatPatientName(matchModal.row.patient_name)}</strong> &mdash; {formatDate(matchModal.row.service_date)} &mdash; {formatCurrency(matchModal.row.total_charge_amount)}
              </p>
            </div>
            <div className="match-modal-search">
              <input
                type="text"
                placeholder="Search by patient name..."
                value={matchSearch}
                onChange={(e) => searchClaimsForMatch(e.target.value)}
                autoFocus
              />
            </div>
            {matchLoading && <div className="match-modal-loading">Searching...</div>}
            {matchResults.length > 0 && (
              <table className="match-results-table">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>DOS</th>
                    <th>Billed</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {matchResults.map((c) => (
                    <tr key={c.id}>
                      <td>{formatPatientName(c.patient_name)}</td>
                      <td>{formatDate(c.date_of_service)}</td>
                      <td className="col-currency">{formatCurrency(c.billed_amount)}</td>
                      <td>
                        <span
                          className="status-badge"
                          style={{ backgroundColor: claimStatusColors[displayStatusKey(c.claim_status, readOnly)] || '#718096' }}
                        >
                          {displayStatusKey(c.claim_status, readOnly) || '—'}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-primary btn-xs"
                          onClick={() => handleManualMatch(matchModal.remittanceId, matchModal.claimIndex, c.id)}
                        >
                          Select
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {matchSearch.length >= 2 && !matchLoading && matchResults.length === 0 && (
              <div className="match-modal-empty">No claims found.</div>
            )}
          </div>
        </div>
      )}

      {entryStep === 'choose' && (
        <div className="modal-overlay" onClick={() => setEntryStep('closed')}>
          <div className="modal-content entry-chooser-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Enter Payment</h3>
              <button className="close-btn" onClick={() => setEntryStep('closed')}>&times;</button>
            </div>
            <p className="entry-chooser-subtitle">How is this payment arriving?</p>
            <div className="entry-chooser-cards">
              <button
                type="button"
                className="entry-chooser-card"
                onClick={() => {
                  setUploadItems([]);
                  setUploadError(null);
                  setEntryStep('upload');
                }}
              >
                <div className="entry-chooser-icon">{uploadIcon}</div>
                <div className="entry-chooser-title">Upload File</div>
                <div className="entry-chooser-sub">.dat X12 835 from a payer portal</div>
              </button>
              <button
                type="button"
                className="entry-chooser-card"
                onClick={() => { resetManualEob(); setEntryStep('manual'); }}
              >
                <div className="entry-chooser-icon">{pencilIcon}</div>
                <div className="entry-chooser-title">Manual Input</div>
                <div className="entry-chooser-sub">Type EOB claim totals by hand</div>
              </button>
            </div>
          </div>
        </div>
      )}

      {entryStep === 'upload' && (
        <div className="modal-overlay" onClick={() => !uploadCommitting && setEntryStep('closed')}>
          <div className="modal-content x12-upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Upload X12 835 Files</h3>
              <button
                className="close-btn"
                onClick={() => setEntryStep('closed')}
                disabled={uploadCommitting}
              >&times;</button>
            </div>

            <div className="manual-eob-section">
              <p className="x12-upload-instructions">
                Upload one or more .dat (X12 835) files. We'll parse each and
                show the claims before saving. You can add more files after
                the first batch.
              </p>
              <input
                type="file"
                accept=".dat,.txt,.835,.x12,application/octet-stream,text/plain"
                multiple
                onChange={(e) => {
                  handleUploadFilesChange(e.target.files);
                  e.target.value = '';
                }}
                disabled={uploadCommitting}
              />
            </div>

            {uploadItems.length > 0 && (
              <div className="manual-eob-section">
                {uploadItems.map((item) => (
                  <div key={item.file.name + item.file.size} className="x12-file-card">
                    <div className="x12-file-card-header">
                      <button
                        className="x12-file-toggle"
                        onClick={() => toggleUploadItemExpanded(item.file)}
                        disabled={item.status !== 'ready'}
                        title={item.status === 'ready' ? 'Click to view claims' : ''}
                      >
                        {item.status === 'ready' ? (item.expanded ? '▾' : '▸') : '○'}
                      </button>
                      <div className="x12-file-card-name">{item.file.name}</div>
                      <div className="x12-file-card-meta">
                        {item.status === 'parsing' && <span className="x12-upload-status">Parsing…</span>}
                        {item.status === 'error' && (
                          <span className="match-status unmatched">{item.error || 'Parse failed'}</span>
                        )}
                        {item.status === 'saving' && <span className="x12-upload-status">Saving…</span>}
                        {item.status === 'posting' && <span className="x12-upload-status">Posting…</span>}
                        {item.status === 'posted' && <span className="match-status matched">Posted</span>}
                        {item.status === 'partial' && <span className="match-status partial">Partial</span>}
                        {item.status === 'unposted' && <span className="match-status unmatched">Unposted</span>}
                        {item.status === 'failed' && (
                          <span className="match-status unmatched">{item.error || 'Failed'}</span>
                        )}
                        {(item.status === 'ready' || item.status === 'saving' || item.status === 'posting') && item.preview && (
                          <>
                            <span> · {item.preview.remittance.payer_name || 'Unknown payer'}</span>
                            <span> · {formatCurrency(item.preview.remittance.total_amount)}</span>
                            <span> · {item.preview.claim_payments.length} claim{item.preview.claim_payments.length === 1 ? '' : 's'}</span>
                          </>
                        )}
                      </div>
                      <button
                        className="close-btn x12-file-remove"
                        onClick={() => removeUploadItem(item.file)}
                        disabled={uploadCommitting}
                        title="Remove this file"
                      >&times;</button>
                    </div>

                    {item.status === 'ready' && item.expanded && item.preview && (
                      <div className="x12-file-card-body">
                        <div className="allocation-summary">
                          <div><strong>Check / EFT:</strong> {item.preview.remittance.check_number || '—'} ({item.preview.remittance.payment_method || '—'})</div>
                          <div><strong>Payment Date:</strong> {formatDate(item.preview.remittance.payment_date)}</div>
                        </div>
                        <table className="remittance-claims-table">
                          <thead>
                            <tr>
                              <th>Patient</th>
                              <th>DOS</th>
                              <th>Claim #</th>
                              <th className="col-currency">Billed</th>
                              <th className="col-currency">Adj</th>
                              <th className="col-currency">Pt Resp</th>
                              <th className="col-currency">Paid</th>
                              <th>Atlas Claim</th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.preview.claim_payments.map((cp, i) => (
                              <tr key={i}>
                                <td>{formatPatientName(cp.patient_name)}</td>
                                <td>{formatDate(cp.service_date)}</td>
                                <td className="col-mono">{cp.patient_control_number || '—'}</td>
                                <td className="col-currency">{formatCurrency(cp.total_charge_amount)}</td>
                                <td className="col-currency">{formatCurrency(cp.co_adjustments)}</td>
                                <td className="col-currency">{formatCurrency(cp.pr_adjustments)}</td>
                                <td className="col-currency">{formatCurrency(cp.claim_payment_amount)}</td>
                                <td>
                                  {cp.auto_match && cp.auto_match.claim_id ? (
                                    <span
                                      className="match-status matched"
                                      title={`${formatPatientName(cp.auto_match.patient_name)} · #${cp.auto_match.claim_id} · ${cp.auto_match.date_of_service || ''}`}
                                    >
                                      Matched
                                    </span>
                                  ) : (
                                    <span className="match-status unmatched">No match</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {uploadError && <div className="manual-eob-error">{uploadError}</div>}

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setEntryStep('choose')}
                disabled={uploadCommitting}
              >Back</button>
              {(() => {
                const ready = uploadItems.filter((it) => it.status === 'ready');
                const parsingCount = uploadItems.filter((it) => it.status === 'parsing').length;
                if (uploadItems.length === 0) return null;
                const disabled = uploadCommitting || ready.length === 0 || parsingCount > 0;
                const parsingLabel = parsingCount > 0 ? `Parsing ${parsingCount}…` : null;
                // Aggregate auto-match counts across all ready files so users
                // know what "Post Matched" will actually do.
                let totalClaims = 0;
                let matchedClaims = 0;
                for (const it of ready) {
                  for (const cp of it.preview?.claim_payments || []) {
                    totalClaims += 1;
                    if (cp.auto_match && cp.auto_match.claim_id) matchedClaims += 1;
                  }
                }
                return (
                  <>
                    <div className="x12-upload-counts">
                      {totalClaims > 0 && (
                        <>
                          <strong>{matchedClaims}</strong> of {totalClaims} claim{totalClaims === 1 ? '' : 's'} matched
                          {matchedClaims < totalClaims && (
                            <span> · {totalClaims - matchedClaims} need manual review</span>
                          )}
                        </>
                      )}
                    </div>
                    <button
                      className="btn btn-secondary"
                      onClick={handleUploadCommit}
                      disabled={disabled}
                    >
                      {uploadCommitting
                        ? (uploadProgress ? `Saving ${uploadProgress.current}/${uploadProgress.total}…` : 'Saving…')
                        : parsingLabel || `Save ${ready.length} as Unposted`}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleUploadCommitAndPost}
                      disabled={disabled}
                      title="Save and immediately post any auto-matched claims to the ledger. Files with unmatched claims will be left in Partial for manual review."
                    >
                      {uploadCommitting
                        ? (uploadProgress ? `Saving & Posting ${uploadProgress.current}/${uploadProgress.total}…` : 'Saving & Posting…')
                        : parsingLabel || `Save & Post ${matchedClaims} Matched`}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {entryStep === 'manual' && (
        <div className="modal-overlay" onClick={() => !manualEobSubmitting && setEntryStep('closed')}>
          <div className="modal-content manual-eob-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Manual EOB Entry</h3>
              <button
                className="modal-close-btn"
                onClick={() => setEntryStep('closed')}
                disabled={manualEobSubmitting}
                aria-label="Close"
              >&times;</button>
            </div>

            <div className="manual-eob-scroll">
              <div className="manual-eob-top">
                <div className="manual-eob-header-fields">
                  <div className="form-row">
                    <label>
                      <span className="field-label">Payer<span className="required">*</span></span>
                      <select
                        value={manualEob.payer_db_id}
                        onChange={(e) => selectEobPayer(e.target.value)}
                      >
                        <option value="">Select payer...</option>
                        {(selectedPractice?.payers || []).map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.payer_name}{p.payer_id ? ` (${p.payer_id})` : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span className="field-label">Payment Date<span className="required">*</span></span>
                      <input
                        type="date"
                        min={DATE_INPUT_MIN}
                        max={DATE_INPUT_MAX}
                        value={manualEob.payment_date}
                        onChange={(e) => updateManualEobField('payment_date', e.target.value)}
                      />
                    </label>
                    <label>
                      <span className="field-label">Total Amount<span className="required">*</span></span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={manualEob.total_amount}
                        onChange={(e) => updateManualEobField('total_amount', e.target.value)}
                      />
                    </label>
                  </div>
                  <div className="form-row">
                    <label>
                      <span className="field-label">Method</span>
                      <select
                        value={manualEob.payment_method}
                        onChange={(e) => updateManualEobField('payment_method', e.target.value)}
                      >
                        <option value="CHK">Check</option>
                        <option value="ACH">ACH / EFT</option>
                        <option value="CASH">Cash</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </label>
                    <label>
                      <span className="field-label">Check / EFT #</span>
                      <input
                        type="text"
                        value={manualEob.check_number}
                        onChange={(e) => updateManualEobField('check_number', e.target.value)}
                      />
                    </label>
                  </div>
                </div>

                <div className="manual-eob-log">
                  <div className="manual-eob-log-header">Posting Log</div>
                  {eobPostingLog.length === 0 ? (
                    <div className="manual-eob-log-empty">Posts will appear here as you log them.</div>
                  ) : (
                    <table className="manual-eob-log-table">
                      <thead>
                        <tr>
                          <th>Patient</th>
                          <th>DOS</th>
                          <th className="num">Pmt</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {eobPostingLog.map((entry, i) => (
                          <tr key={i}>
                            <td>{entry.patient_name}</td>
                            <td>{entry.date_of_service || '—'}</td>
                            <td className="num">{(entry.paid || 0).toFixed(2)}</td>
                            <td>
                              <button
                                className="log-remove-btn"
                                onClick={() => removePostingLogEntry(i)}
                                title="Remove from log"
                                aria-label="Remove"
                              >&times;</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {(() => {
                    const applied = eobPostingLog.reduce((s, e) => s + (parseFloat(e.paid) || 0), 0);
                    const total = parseFloat(manualEob.total_amount) || 0;
                    const remaining = Math.round((total - applied) * 100) / 100;
                    return (
                      <div className="manual-eob-log-totals">
                        <div><span>Applied</span><strong>{applied.toFixed(2)}</strong></div>
                        <div><span>Total</span><strong>{total.toFixed(2)}</strong></div>
                        <div className={remaining < 0 ? 'over' : ''}>
                          <span>Remaining</span><strong>{remaining.toFixed(2)}</strong>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="manual-eob-body">
                <div className="form-row eob-patient-row">
                  <label className="full-width eob-patient-search">
                    <span className="field-label">Patient</span>
                    <input
                      type="text"
                      value={eobPatientSearch}
                      onChange={(e) => searchEobPatients(e.target.value)}
                      placeholder={manualEob.payer_db_id
                        ? 'Type a patient name to load open claim lines...'
                        : 'Select a payer first to enable patient search'}
                      autoComplete="off"
                      disabled={!manualEob.payer_db_id}
                    />
                    {eobPatientSuggestions.length > 0
                      && eobPatientSearch.trim().toLowerCase() !== (eobActivePatient || '').toLowerCase() && (
                      <div className="eob-patient-dropdown">
                        {eobPatientSuggestions.map((name) => (
                          <button
                            key={name}
                            type="button"
                            className="eob-patient-option"
                            onClick={() => loadEobLinesForPatient(name)}
                          >{name}</button>
                        ))}
                      </div>
                    )}
                    {manualEob.payer_db_id
                      && eobPatientSearch.trim().length >= 2
                      && eobPatientSuggestions.length === 0
                      && !eobActivePatient
                      && !eobLinesLoading && (
                      <div className="eob-patient-hint">No open claims found for this patient under {manualEob.payer_name}.</div>
                    )}
                  </label>
                </div>

                {eobLinesLoading && <div className="eob-loading">Loading claim lines...</div>}
                {!eobLinesLoading && eobActivePatient && eobActiveLines.length === 0 && (
                  <div className="eob-empty">No open claim lines found for this patient under {manualEob.payer_name}.</div>
                )}
                {!eobLinesLoading && eobActiveLines.length > 0 && (
                  <>
                    <table className="manual-eob-lines">
                      <thead>
                        <tr>
                          <th>Proc</th>
                          <th>DOS</th>
                          <th>Charge</th>
                          <th>Remaining</th>
                          <th>Allowed</th>
                          <th>Ins Paid</th>
                          <th>Pt Resp</th>
                          <th>Adj</th>
                          <th>Balance</th>
                        </tr>
                      </thead>
                      {(() => {
                        const indexed = eobActiveLines.map((line, idx) => ({ ...line, _index: idx }));
                        const groups = new Map();
                        for (const line of indexed) {
                          if (!groups.has(line.claim_id)) {
                            groups.set(line.claim_id, {
                              claim_id: line.claim_id,
                              dos: line.date_of_service,
                              status: line.claim_status,
                              ccn: line.claim_control_number,
                              lines: [],
                            });
                          }
                          groups.get(line.claim_id).lines.push(line);
                        }
                        return Array.from(groups.values()).map((g) => {
                          const totalCharge = g.lines.reduce((s, l) => s + (l.charge || 0), 0);
                          const totalRemaining = g.lines.reduce((s, l) => s + (l.remaining || 0), 0);
                          return (
                            <tbody key={g.claim_id} className="manual-eob-case">
                              <tr className="manual-eob-case-header">
                                <td colSpan={9}>
                                  <strong>DOS {g.dos || '—'}</strong>
                                  <span className="case-meta">{g.status}</span>
                                  {g.ccn && <span className="case-meta">Claim #{g.ccn}</span>}
                                  <span className="case-meta">Charged ${totalCharge.toFixed(2)}</span>
                                  <span className="case-meta">Remaining ${totalRemaining.toFixed(2)}</span>
                                </td>
                              </tr>
                              {g.lines.map((line) => {
                                const paid = parseFloat(line._paid) || 0;
                                const ptResp = parseFloat(line._pt_resp) || 0;
                                const adj = parseFloat(line._adj) || 0;
                                const balance = Math.max(
                                  Math.round((line.remaining - paid - ptResp - adj) * 100) / 100,
                                  0
                                );
                                const i = line._index;
                                return (
                                  <tr key={`${line.claim_id}-${line.cpt_index}`}>
                                    <td className="col-mono center">{line.cpt_code}</td>
                                    <td className="center">{line.date_of_service || '—'}</td>
                                    <td className="num">{(line.charge || 0).toFixed(2)}</td>
                                    <td className="num">{(line.remaining || 0).toFixed(2)}</td>
                                    <td className="num">
                                      <input type="number" step="0.01" value={line._allowed}
                                        onChange={(e) => updateEobActiveLine(i, '_allowed', e.target.value)} />
                                    </td>
                                    <td className="num">
                                      <input type="number" step="0.01" value={line._paid}
                                        onChange={(e) => updateEobActiveLine(i, '_paid', e.target.value)} />
                                    </td>
                                    <td className="num">
                                      <input type="number" step="0.01" value={line._pt_resp}
                                        onChange={(e) => updateEobActiveLine(i, '_pt_resp', e.target.value)} />
                                    </td>
                                    <td className="num">
                                      <input type="number" step="0.01" value={line._adj}
                                        onChange={(e) => updateEobActiveLine(i, '_adj', e.target.value)}
                                        className={line._adj_manual ? 'manual-override' : ''}
                                        title={line._adj_manual ? 'Manually overridden — clear to resume auto' : 'Auto-computed from Charge − Allowed (or Charge − Ins Paid − Pt Resp)'}
                                      />
                                    </td>
                                    <td className="num">{balance.toFixed(2)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          );
                        });
                      })()}
                    </table>
                    <div className="eob-post-row">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={postCurrentPatient}
                      >Post</button>
                    </div>
                  </>
                )}
              </div>

              {manualEobError && (
                <div className="manual-eob-error">{manualEobError}</div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setEntryStep('choose')}
                disabled={manualEobSubmitting}
              >Back</button>
              <button
                className="btn btn-primary"
                onClick={handleManualEobSubmit}
                disabled={manualEobSubmitting || eobPostingLog.length === 0}
              >
                {manualEobSubmitting ? 'Saving...' : 'Save (Unposted)'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editRem && (
        <div className="modal-overlay" onClick={() => !editRemSaving && setEditRem(null)}>
          <div className="modal-content edit-remittance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Remittance</h3>
              <button
                className="modal-close-btn"
                onClick={() => setEditRem(null)}
                disabled={editRemSaving}
                aria-label="Close"
              >&times;</button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <div className="form-row">
                <label>
                  <span className="field-label">Payer</span>
                  <input
                    type="text"
                    value={editRem.payer_name}
                    onChange={(e) => updateEditRemField('payer_name', e.target.value)}
                  />
                </label>
                <label>
                  <span className="field-label">Payment Date</span>
                  <input
                    type="date"
                    min={DATE_INPUT_MIN}
                    max={DATE_INPUT_MAX}
                    value={editRem.payment_date}
                    onChange={(e) => updateEditRemField('payment_date', e.target.value)}
                  />
                </label>
              </div>
              <div className="form-row">
                <label>
                  <span className="field-label">Method</span>
                  <select
                    value={editRem.payment_method}
                    onChange={(e) => updateEditRemField('payment_method', e.target.value)}
                  >
                    <option value="CHK">Check</option>
                    <option value="ACH">ACH / EFT</option>
                    <option value="CASH">Cash</option>
                    <option value="OTHER">Other</option>
                  </select>
                </label>
                <label>
                  <span className="field-label">Check / EFT #</span>
                  <input
                    type="text"
                    value={editRem.check_number}
                    onChange={(e) => updateEditRemField('check_number', e.target.value)}
                  />
                </label>
              </div>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '8px 0 0' }}>
                Header-only edit. Changes to payment date and payer cascade to linked claims and adjudications. Money and line items aren't editable here — Reverse and re-create the remittance to change those.
              </p>
              {editRemError && (
                <div className="manual-eob-error" style={{ marginTop: 10 }}>{editRemError}</div>
              )}
            </div>
            <div className="modal-footer" style={{ padding: '12px 20px', borderTop: '1px solid var(--border-color)' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setEditRem(null)}
                disabled={editRemSaving}
              >Cancel</button>
              <button
                className="btn btn-primary"
                onClick={submitEditRemittance}
                disabled={editRemSaving}
              >
                {editRemSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPatientPmt && (
        <div className="modal-overlay" onClick={() => !pmtSubmitting && setShowPatientPmt(false)}>
          <div className="modal-content patient-pmt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Record Patient Payment {pmtStep === 1 ? '(1 of 2)' : '(2 of 2)'}</h3>
              <button
                className="close-btn"
                onClick={() => setShowPatientPmt(false)}
                disabled={pmtSubmitting}
              >&times;</button>
            </div>

            {pmtStep === 1 && (
              <div className="manual-eob-section">
                <div className="form-row">
                  <div className="patient-field">
                    <span className="field-label">Patient<span className="required">*</span></span>
                    {pmtForm.practice_patient_id ? (
                      <div className="picked-patient">
                        <span>{pmtForm.patient_label}</span>
                        <button
                          type="button"
                          className="btn btn-secondary btn-xs"
                          onClick={() => setPmtForm((p) => ({ ...p, practice_patient_id: null, patient_label: '' }))}
                        >Change</button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={pmtPatientSearch}
                          onChange={(e) => searchPatients(e.target.value)}
                          placeholder="Search by name (min 2 chars)"
                          autoFocus
                        />
                        {pmtPatientSearching && <div className="patient-search-status">Searching...</div>}
                        {pmtPatientResults.length > 0 && (
                          <div className="patient-search-results">
                            {pmtPatientResults.map((p) => {
                              const label = (p.subscriber_first_name || p.patient_first_name || '') + ' ' + (p.subscriber_last_name || p.patient_last_name || '');
                              return (
                                <div
                                  key={p.id}
                                  className="patient-search-result"
                                  onMouseDown={(e) => { e.preventDefault(); selectPatient(p); }}
                                >
                                  {label.trim()}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <label>
                    <span className="field-label">Amount<span className="required">*</span></span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={pmtForm.amount}
                      onChange={(e) => setPmtForm((p) => ({ ...p, amount: e.target.value }))}
                    />
                  </label>
                  <label>
                    <span className="field-label">Payment Date<span className="required">*</span></span>
                    <input
                      type="date"
                      min={DATE_INPUT_MIN}
                      max={DATE_INPUT_MAX}
                      value={pmtForm.payment_date}
                      onChange={(e) => setPmtForm((p) => ({ ...p, payment_date: e.target.value }))}
                    />
                  </label>
                </div>

                <div className="form-row">
                  <label>
                    <span className="field-label">Method<span className="required">*</span></span>
                    <select
                      value={pmtForm.method}
                      onChange={(e) => setPmtForm((p) => ({ ...p, method: e.target.value }))}
                    >
                      <option value="cash">Cash</option>
                      <option value="card">Credit / Debit</option>
                      <option value="check">Check</option>
                    </select>
                  </label>
                  <label>
                    <span className="field-label">Confirmation #</span>
                    <input
                      type="text"
                      value={pmtForm.reference_number}
                      onChange={(e) => setPmtForm((p) => ({ ...p, reference_number: e.target.value }))}
                      placeholder="optional"
                    />
                  </label>
                  <label>
                    <span className="field-label">Notes</span>
                    <input
                      type="text"
                      value={pmtForm.notes}
                      onChange={(e) => setPmtForm((p) => ({ ...p, notes: e.target.value }))}
                      placeholder="optional"
                    />
                  </label>
                </div>
              </div>
            )}

            {pmtStep === 2 && (
              <div className="manual-eob-section">
                <div className="allocation-summary">
                  <div><strong>Patient:</strong> {pmtForm.patient_label}</div>
                  <div><strong>Payment:</strong> {formatCurrency(pmtAmount)} ({pmtForm.method})</div>
                  <div><strong>Allocated:</strong> {formatCurrency(allocationSum)} · <strong>Remaining:</strong> {formatCurrency(pmtAmount - allocationSum)}</div>
                </div>

                {pmtAllocations.length === 0 ? (
                  <div className="remittances-empty">
                    <p>No open patient-responsibility claims found for this patient.</p>
                    <p>You can still record the payment, but it won't apply to any claims.</p>
                  </div>
                ) : (
                  <table className="remittance-claims-table">
                    <thead>
                      <tr>
                        <th>Claim DOS</th>
                        <th className="col-currency">Billed</th>
                        <th className="col-currency">Balance</th>
                        <th className="col-currency">Apply</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pmtAllocations.map((a, i) => (
                        <tr key={a.claim_id}>
                          <td>{formatDate(a.date_of_service)}</td>
                          <td className="col-currency">{formatCurrency(a.billed_amount)}</td>
                          <td className="col-currency">{formatCurrency(a.balance_before)}</td>
                          <td className="col-currency">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max={a.balance_before}
                              value={a.applied}
                              onChange={(e) => updateAllocationAmount(i, e.target.value)}
                              style={{ width: '90px', textAlign: 'right' }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {pmtError && <div className="manual-eob-error">{pmtError}</div>}

            <div className="modal-footer">
              {pmtStep === 1 ? (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowPatientPmt(false)}
                    disabled={pmtSubmitting}
                  >Cancel</button>
                  <button
                    className="btn btn-primary"
                    onClick={goToAllocationStep}
                    disabled={pmtPreviewLoading}
                  >
                    {pmtPreviewLoading ? 'Loading...' : 'Next'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setPmtStep(1)}
                    disabled={pmtSubmitting}
                  >Back</button>
                  <button
                    className="btn btn-primary"
                    onClick={handlePatientPmtSubmit}
                    disabled={pmtSubmitting || allocationSum > pmtAmount + 0.005}
                  >
                    {pmtSubmitting ? 'Saving...' : 'Save Payment'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showInvoice && (
        <div className="modal-overlay" onClick={() => !invoiceSubmitting && setShowInvoice(false)}>
          <div className="modal-content invoice-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Generate Invoice</h3>
              <button className="close-btn" onClick={() => setShowInvoice(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="invoice-modal-subtitle">
                Lists ERAs received in the selected period and applies the rate to total collections.
              </p>
              <div className="form-row">
                <label>
                  Start date
                  <input
                    type="date"
                    min={DATE_INPUT_MIN}
                    max={DATE_INPUT_MAX}
                    value={invoiceForm.start_date}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, start_date: e.target.value })}
                  />
                </label>
                <label>
                  End date
                  <input
                    type="date"
                    min={DATE_INPUT_MIN}
                    max={DATE_INPUT_MAX}
                    value={invoiceForm.end_date}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, end_date: e.target.value })}
                  />
                </label>
              </div>
              <div className="form-row">
                <label>
                  Rate (%)
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="e.g. 8"
                    value={invoiceForm.rate_pct}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, rate_pct: e.target.value })}
                  />
                </label>
              </div>
              {invoiceError && (
                <div className="remittances-message error">
                  <span>{invoiceError}</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowInvoice(false)}
                disabled={invoiceSubmitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleInvoiceSubmit}
                disabled={invoiceSubmitting}
              >
                {invoiceSubmitting ? 'Generating...' : 'Generate Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentsPage;
